import { useCollectionClient } from "providers/CollectionClientContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type CollectionMembership,
  type CollectionRecord,
  type CreateCollectionBody,
  getSavedStateStore,
  type MaterialRef,
} from "service/collections";

export type CollectionBookmarkStatus = "idle" | "loading" | "ready" | "error";

/** What {@link useCollectionBookmark}'s `create` resolves with. */
export interface CreateCollectionOutcome {
  collection: CollectionRecord;
  /** The collection exists, but saving the material into it failed. */
  saveFailed: boolean;
  /** The collection exists, but its moodboard image failed to upload. */
  moodboardFailed: boolean;
}

const byName = (a: CollectionMembership, b: CollectionMembership) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

/**
 * Saved-state + membership actions for one Color Finder material.
 *
 * The heart's filled state comes from the shared {@link getSavedStateStore}
 * (cached, concurrency-limited) as soon as the card mounts. The full collection
 * list loads only while `enabled` (the popover is open), fresh on every open;
 * once it has loaded, it is the source of truth and is written back to the
 * store. Toggles are optimistic: the row flips first and reverts (then
 * rethrows) if the call fails.
 *
 * @param eljExperimentId - The material (`sourceRefId` for `COLOR_FINDER`).
 * @param enabled - Load the collection list (true while the popover is open).
 */
export const useCollectionBookmark = (
  eljExperimentId: string,
  enabled: boolean,
) => {
  const client = useCollectionClient();
  const store = getSavedStateStore(client);
  const material = useMemo<MaterialRef>(
    () => ({
      sourceDiscriminator: "COLOR_FINDER",
      sourceRefId: eljExperimentId,
    }),
    [eljExperimentId],
  );
  const [status, setStatus] = useState<CollectionBookmarkStatus>("idle");
  const [rows, setRows] = useState<CollectionMembership[]>([]);
  const [listLoaded, setListLoaded] = useState(false);
  const [savedHint, setSavedHint] = useState(false);
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  // Only the latest lookup may write, so a stale response can't overwrite a newer one.
  const requestRef = useRef(0);
  const listLoadedRef = useRef(false);

  useEffect(() => {
    let active = true;
    store.get(material).then(
      (saved) => {
        if (active) {
          setSavedHint(saved);
        }
      },
      // A failed lookup leaves the heart unfilled; opening the popover loads
      // the list and surfaces the error with Retry there.
      () => undefined,
    );
    return () => {
      active = false;
    };
  }, [store, material]);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    // A refresh on re-open keeps the loaded rows on screen; a first load or a
    // retry after an error shows the loader.
    setStatus((prev) =>
      listLoadedRef.current && prev === "ready" ? "ready" : "loading",
    );
    try {
      const result = await client.getMembership(material);
      if (request === requestRef.current) {
        listLoadedRef.current = true;
        setRows(result);
        setListLoaded(true);
        setStatus("ready");
      }
    } catch {
      if (request === requestRef.current) {
        setStatus("error");
      }
    }
  }, [client, material]);

  useEffect(() => {
    if (enabled) {
      void load();
    }
  }, [enabled, load]);

  const patch = (collectionId: string, change: Partial<CollectionMembership>) =>
    setRows((prev) =>
      prev.map((row) =>
        row.collectionId === collectionId ? { ...row, ...change } : row,
      ),
    );

  const setPending = (collectionId: string, pending: boolean) =>
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (pending) {
        next.add(collectionId);
      } else {
        next.delete(collectionId);
      }
      return next;
    });

  const add = async (row: CollectionMembership) => {
    setPending(row.collectionId, true);
    patch(row.collectionId, { contains: true });
    try {
      const record = await client.addMaterial(row.collectionId, material);
      patch(row.collectionId, { membershipId: record.membershipId });
    } catch (error) {
      patch(row.collectionId, { contains: false, membershipId: undefined });
      throw error;
    } finally {
      setPending(row.collectionId, false);
    }
  };

  const remove = async (row: CollectionMembership) => {
    if (!row.membershipId) {
      return;
    }
    setPending(row.collectionId, true);
    patch(row.collectionId, { contains: false, membershipId: undefined });
    try {
      await client.removeMaterial(row.collectionId, row.membershipId);
    } catch (error) {
      patch(row.collectionId, {
        contains: true,
        membershipId: row.membershipId,
      });
      throw error;
    } finally {
      setPending(row.collectionId, false);
    }
  };

  /**
   * Creates a collection, saves the material into it (AC4), then uploads the
   * optional first moodboard image. Only a failed create rejects: once the
   * collection exists a retry would duplicate it, so a failed save or upload
   * comes back as a flag instead.
   */
  const create = async (
    body: CreateCollectionBody,
    moodboardFile?: File,
  ): Promise<CreateCollectionOutcome> => {
    const collection = await client.createCollection(body);
    const row: CollectionMembership = {
      collectionId: collection.id,
      name: collection.name,
      contains: false,
    };
    setRows((prev) => [...prev, row]);
    const saveFailed = await add(row).then(
      () => false,
      () => true,
    );
    const moodboardFailed = moodboardFile
      ? await client.addMoodboardImage(collection.id, moodboardFile).then(
          () => false,
          () => true,
        )
      : false;
    return { collection, saveFailed, moodboardFailed };
  };

  const collections = useMemo(() => [...rows].sort(byName), [rows]);
  const isSaved = listLoaded ? rows.some((row) => row.contains) : savedHint;

  // The loaded list (and every toggle on it) is the newest answer — share it so
  // a re-mounted card for the same material shows it without another lookup.
  useEffect(() => {
    if (listLoaded) {
      store.set(material, isSaved);
    }
  }, [store, material, listLoaded, isSaved]);

  return {
    status,
    collections,
    pendingIds,
    isSaved,
    reload: load,
    add,
    remove,
    create,
  };
};
